<template>
  <AppLayout>
    <div class="site-page">
      <div class="page-header">
        <h1>Site / Chantier</h1>
        <div class="page-actions">
          <BaseButton variant="secondary" @click="openReceptionModal">
            + Nouvelle réception
          </BaseButton>
          <BaseButton variant="primary" @click="openInstallationModal">
            + Nouvelle pose
          </BaseButton>
        </div>
      </div>

      <BaseCard class="filters-card">
        <div class="filters-grid">
          <BaseSelect v-model="selectedProjetId" label="Projet">
            <option value="">-- Sélectionner --</option>
            <option v-for="projet in projets" :key="projet.id" :value="String(projet.id)">
              {{ projet.nom }} ({{ projet.code }})
            </option>
          </BaseSelect>

          <BaseSelect v-model="selectedSiteId" label="Site">
            <option value="">Tous les sites</option>
            <option v-for="site in filteredSites" :key="site.id" :value="String(site.id)">
              {{ site.nom }}
            </option>
          </BaseSelect>

          <BaseInput
            v-model="selectedDate"
            type="date"
            label="Filtre date"
          />
        </div>
      </BaseCard>

      <div v-if="error" class="error">{{ error }}</div>

      <div class="kpis-grid">
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.recu }}</div>
          <div class="kpi-label">Reçu</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.stockSite }}</div>
          <div class="kpi-label">Stock site</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.pose }}</div>
          <div class="kpi-label">Posé</div>
        </BaseCard>
        <BaseCard class="kpi-card">
          <div class="kpi-value">{{ kpis.tauxPose }}%</div>
          <div class="kpi-label">Taux pose</div>
        </BaseCard>
      </div>

      <section class="panel-section">
        <div class="section-header">
          <h2>Flux quantités (site)</h2>
        </div>
        <BaseTable
          :columns="fluxColumns"
          :items="fluxRows"
          :loading="loadingFlux"
          :show-actions="false"
        />
      </section>

      <section class="panel-section">
        <div class="section-header">
          <h2>Réceptions</h2>
        </div>
        <BaseTable
          :columns="receptionColumns"
          :items="receptionsFiltered"
          :loading="loadingReceptions"
          :show-actions="true"
          @view="viewReception"
        >
          <template #cell-dateReception="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-statut="{ value }">
            <BaseBadge :variant="getReceptionStatutVariant(value)">{{ value }}</BaseBadge>
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="viewReception(item)">👁️ Voir</BaseButton>
          </template>
        </BaseTable>
      </section>

      <section class="panel-section">
        <div class="section-header">
          <h2>Poses</h2>
        </div>
        <BaseTable
          :columns="installationColumns"
          :items="installationsFiltered"
          :loading="loadingInstallations"
          :show-actions="true"
        >
          <template #cell-dateInstallation="{ value }">
            {{ formatDate(value) }}
          </template>
          <template #cell-typeElement="{ item }">
            {{ resolveTypeLabel(item.typeElementPrefabriqueId) }}
          </template>
          <template #actions="{ item }">
            <BaseButton variant="ghost" size="sm" @click="openEditInstallationModal(item)">Modifier</BaseButton>
            <BaseButton variant="ghost" size="sm" @click="deleteInstallation(item.id)">Supprimer</BaseButton>
          </template>
        </BaseTable>
      </section>
    </div>

    <Modal v-if="showReceptionModal" @close="closeReceptionModal">
      <template #title>Nouvelle réception</template>
      <form class="modal-form" @submit.prevent="saveReception">
        <BaseSelect v-model="receptionForm.expeditionId" label="Expédition *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="expedition in availableExpeditions" :key="expedition.id" :value="String(expedition.id)">
            #{{ expedition.id }} - {{ formatDate(expedition.dateExpedition) }} ({{ expedition.statut }})
          </option>
        </BaseSelect>

        <BaseInput
          v-model="receptionForm.dateReception"
          label="Date réception *"
          type="date"
        />

        <BaseSelect v-model="receptionForm.statut" label="Statut">
          <option value="OK">OK</option>
          <option value="AVEC_DOMMAGES">Avec dommages</option>
          <option value="PARTIEL">Partiel</option>
        </BaseSelect>

        <div class="items-header">
          <h3>Items réception</h3>
          <BaseButton type="button" variant="ghost" size="sm" @click="addReceptionItemRow">
            + Ligne
          </BaseButton>
        </div>

        <div v-for="item in receptionItems" :key="item.uid" class="reception-item-row">
          <BaseSelect v-model="item.typeElementPrefabriqueId" label="Type">
            <option value="">-- Sélectionner --</option>
            <option v-for="type in typesElements" :key="type.id" :value="String(type.id)">
              {{ type.libelle }} ({{ type.code }})
            </option>
          </BaseSelect>
          <BaseInput v-model="item.quantiteEnvoyee" label="Envoyée" type="number" placeholder="0" />
          <BaseInput v-model="item.quantiteRecue" label="Reçue" type="number" placeholder="0" />
          <BaseInput v-model="item.quantiteEndommagee" label="Endommagée" type="number" placeholder="0" />
          <BaseInput v-model="item.quantiteManquante" label="Manquante" type="number" placeholder="0" />
          <BaseButton
            type="button"
            variant="ghost"
            size="sm"
            :disabled="receptionItems.length <= 1"
            @click="removeReceptionItemRow(item.uid)"
          >
            Supprimer
          </BaseButton>
        </div>

        <div v-if="receptionFormError" class="form-error">{{ receptionFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeReceptionModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingReception">Enregistrer</BaseButton>
        </div>
      </form>
    </Modal>

    <Modal v-if="showInstallationModal" @close="closeInstallationModal">
      <template #title>{{ editingInstallation ? 'Modifier la pose' : 'Nouvelle pose' }}</template>
      <form class="modal-form" @submit.prevent="saveInstallation">
        <BaseSelect v-model="installationForm.siteId" label="Site *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="site in installationFormSites" :key="site.id" :value="String(site.id)">
            {{ site.nom }}
          </option>
        </BaseSelect>

        <BaseSelect v-model="installationForm.typeElementPrefabriqueId" label="Type d'élément *" required>
          <option value="">-- Sélectionner --</option>
          <option v-for="type in typesElements" :key="type.id" :value="String(type.id)">
            {{ type.libelle }} ({{ type.code }})
          </option>
        </BaseSelect>

        <BaseInput
          v-model="installationForm.quantiteInstallee"
          label="Quantité posée *"
          type="number"
          placeholder="0"
        />

        <BaseInput
          v-model="installationForm.dateInstallation"
          label="Date pose *"
          type="date"
        />

        <div v-if="installationFormError" class="form-error">{{ installationFormError }}</div>
        <div class="form-actions">
          <BaseButton type="button" variant="ghost" @click="closeInstallationModal">Annuler</BaseButton>
          <BaseButton type="submit" variant="primary" :loading="savingInstallation">Enregistrer</BaseButton>
        </div>
      </form>
    </Modal>
  </AppLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import { useRouter } from 'vue-router';
import AppLayout from '../components/AppLayout.vue';
import BaseBadge from '../components/BaseBadge.vue';
import BaseButton from '../components/BaseButton.vue';
import BaseCard from '../components/BaseCard.vue';
import BaseInput from '../components/BaseInput.vue';
import BaseSelect from '../components/BaseSelect.vue';
import BaseTable, { type TableColumn } from '../components/BaseTable.vue';
import Modal from '../components/Modal.vue';
import { apiFetch } from '../utils/api';

interface Projet {
  id: number;
  nom: string;
  code: string;
}

interface Site {
  id: number;
  nom: string;
  projetId: number;
}

interface TypeElementPrefabrique {
  id: number;
  code: string;
  libelle: string;
}

interface Expedition {
  id: number;
  dateExpedition: string;
  statut: string;
}

interface Reception {
  id: number;
  expeditionId: number;
  siteId: number;
  siteNom?: string;
  projetId?: number;
  projetNom?: string;
  projetCode?: string;
  dateReception: string;
  statut: 'OK' | 'AVEC_DOMMAGES' | 'PARTIEL';
}

interface Installation {
  id: number;
  siteId: number;
  siteNom?: string;
  projetId?: number;
  projetNom?: string;
  projetCode?: string;
  typeElementPrefabriqueId: number;
  quantiteInstallee: number;
  dateInstallation: string;
}

interface FluxElement {
  typeElementPrefabriqueId: number;
  type: string;
  designation: string;
  quantiteRecue: number;
  quantiteEndommagee: number;
  quantiteManquante: number;
  quantiteStockSite: number;
  quantitePosee: number;
}

interface ReceptionItemDraft {
  uid: number;
  typeElementPrefabriqueId: string;
  quantiteEnvoyee: string;
  quantiteRecue: string;
  quantiteEndommagee: string;
  quantiteManquante: string;
}

const router = useRouter();

const projets = ref<Projet[]>([]);
const sites = ref<Site[]>([]);
const typesElements = ref<TypeElementPrefabrique[]>([]);
const receptions = ref<Reception[]>([]);
const installations = ref<Installation[]>([]);
const fluxElements = ref<FluxElement[]>([]);
const availableExpeditions = ref<Expedition[]>([]);

const selectedProjetId = ref('');
const selectedSiteId = ref('');
const selectedDate = ref('');

const loadingReceptions = ref(false);
const loadingInstallations = ref(false);
const loadingFlux = ref(false);
const error = ref<string | null>(null);

const showReceptionModal = ref(false);
const savingReception = ref(false);
const receptionFormError = ref<string | null>(null);

const showInstallationModal = ref(false);
const savingInstallation = ref(false);
const installationFormError = ref<string | null>(null);
const editingInstallation = ref<Installation | null>(null);

const receptionForm = reactive({
  expeditionId: '',
  dateReception: toDateInput(new Date()),
  statut: 'OK'
});

const receptionItems = ref<ReceptionItemDraft[]>([
  {
    uid: 1,
    typeElementPrefabriqueId: '',
    quantiteEnvoyee: '',
    quantiteRecue: '',
    quantiteEndommagee: '0',
    quantiteManquante: '0'
  }
]);
const nextReceptionItemUid = ref(2);

const installationForm = reactive({
  siteId: '',
  typeElementPrefabriqueId: '',
  quantiteInstallee: '',
  dateInstallation: toDateInput(new Date())
});

const fluxColumns: TableColumn[] = [
  { key: 'type', label: 'Type' },
  { key: 'designation', label: 'Désignation' },
  { key: 'quantiteRecue', label: 'Reçue' },
  { key: 'quantiteEndommagee', label: 'Endommagée' },
  { key: 'quantiteManquante', label: 'Manquante' },
  { key: 'quantiteStockSite', label: 'Stock site' },
  { key: 'quantitePosee', label: 'Posée' }
];

const receptionColumns: TableColumn[] = [
  { key: 'dateReception', label: 'Date' },
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'expeditionId', label: 'Expédition' },
  { key: 'statut', label: 'Statut' }
];

const installationColumns: TableColumn[] = [
  { key: 'dateInstallation', label: 'Date' },
  { key: 'projetNom', label: 'Projet' },
  { key: 'siteNom', label: 'Site' },
  { key: 'typeElement', label: 'Type' },
  { key: 'quantiteInstallee', label: 'Qté posée' }
];

const filteredSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const scoped = projetId === null ? sites.value : sites.value.filter((site) => site.projetId === projetId);
  return [...scoped].sort((a, b) => a.nom.localeCompare(b.nom, 'fr'));
});

const installationFormSites = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  if (projetId === null) {
    return [] as Site[];
  }
  return sites.value.filter((site) => site.projetId === projetId);
});

const receptionsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return receptions.value.filter((reception) => {
    if (projetId !== null && reception.projetId !== projetId) {
      return false;
    }
    if (siteId !== null && reception.siteId !== siteId) {
      return false;
    }
    if (selectedDate.value && reception.dateReception !== selectedDate.value) {
      return false;
    }
    return true;
  });
});

const installationsFiltered = computed(() => {
  const projetId = toNumberOrNull(selectedProjetId.value);
  const siteId = toNumberOrNull(selectedSiteId.value);

  return installations.value.filter((installation) => {
    if (projetId !== null && installation.projetId !== projetId) {
      return false;
    }
    if (siteId !== null && installation.siteId !== siteId) {
      return false;
    }
    if (selectedDate.value && installation.dateInstallation !== selectedDate.value) {
      return false;
    }
    return true;
  });
});

const fluxRows = computed(() => fluxElements.value);

const kpis = computed(() => {
  const recu = fluxRows.value.reduce((sum, row) => sum + row.quantiteRecue, 0);
  const stockSite = fluxRows.value.reduce((sum, row) => sum + row.quantiteStockSite, 0);
  const pose = fluxRows.value.reduce((sum, row) => sum + row.quantitePosee, 0);
  const tauxPose = recu > 0 ? Math.round((pose / recu) * 100) : 0;
  return { recu, stockSite, pose, tauxPose };
});

watch(
  () => selectedProjetId.value,
  () => {
    const projetId = toNumberOrNull(selectedProjetId.value);
    const siteId = toNumberOrNull(selectedSiteId.value);
    const projetSites = sites.value.filter((site) => site.projetId === projetId);
    const siteToujoursValide =
      siteId !== null && sites.value.some((site) => site.id === siteId && site.projetId === projetId);

    if (!siteToujoursValide) {
      selectedSiteId.value = projetSites.length > 0 ? String(projetSites[0].id) : '';
    }
  }
);

watch(
  () => [selectedProjetId.value, selectedSiteId.value],
  () => {
    loadFluxData();
    loadAvailableExpeditions();
  }
);

onMounted(async () => {
  await loadProjetsAndSites();
  await Promise.all([loadTypesElements(), loadReceptions(), loadInstallations()]);

  if (projets.value.length > 0) {
    selectedProjetId.value = String(projets.value[0].id);
    const firstProjetSite = sites.value.find((site) => site.projetId === projets.value[0].id);
    selectedSiteId.value = firstProjetSite ? String(firstProjetSite.id) : '';
  }
  await Promise.all([loadFluxData(), loadAvailableExpeditions()]);
});

async function loadProjetsAndSites() {
  try {
    const response = await apiFetch('/projets');
    projets.value = (response as { items: Projet[] }).items || [];

    const siteRequests = projets.value.map(async (projet) => {
      const res = await apiFetch(`/projets/${projet.id}/sites`);
      const items = (res as { items: Array<{ id: number; nom: string }> }).items || [];
      return items.map((site) => ({
        id: site.id,
        nom: site.nom,
        projetId: projet.id
      })) satisfies Site[];
    });
    sites.value = (await Promise.all(siteRequests)).flat();
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des projets/sites';
  }
}

async function loadTypesElements() {
  try {
    const response = await apiFetch('/types-elements-prefabriques');
    typesElements.value = (response as { items: TypeElementPrefabrique[] }).items || [];
  } catch (err) {
    console.error('Erreur lors du chargement des types:', err);
  }
}

async function loadReceptions() {
  loadingReceptions.value = true;
  try {
    const response = await apiFetch('/receptions/receptions-site?limit=500&offset=0');
    receptions.value = (response as { items: Reception[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des réceptions';
  } finally {
    loadingReceptions.value = false;
  }
}

async function loadInstallations() {
  loadingInstallations.value = true;
  try {
    const response = await apiFetch('/installations/installations-site?limit=500&offset=0');
    installations.value = (response as { items: Installation[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement des poses';
  } finally {
    loadingInstallations.value = false;
  }
}

async function loadFluxData() {
  const projetId = toNumberOrNull(selectedProjetId.value);
  if (projetId === null) {
    fluxElements.value = [];
    return;
  }

  const siteId = toNumberOrNull(selectedSiteId.value);
  loadingFlux.value = true;
  try {
    const params = new URLSearchParams();
    if (siteId !== null) {
      params.set('siteId', String(siteId));
    }
    const response = await apiFetch(
      `/metriques/projets/${projetId}/flux-elements${params.toString() ? `?${params.toString()}` : ''}`
    );
    fluxElements.value = (response as { items: FluxElement[] }).items || [];
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors du chargement du flux site';
    fluxElements.value = [];
  } finally {
    loadingFlux.value = false;
  }
}

async function loadAvailableExpeditions() {
  const siteId = toNumberOrNull(selectedSiteId.value);
  if (siteId === null) {
    availableExpeditions.value = [];
    return;
  }
  try {
    const response = await apiFetch(`/sites/${siteId}/expeditions`);
    const list = (response as { items: Expedition[] }).items || [];
    availableExpeditions.value = list.filter((expedition) => expedition.statut !== 'ANNULE');
  } catch {
    availableExpeditions.value = [];
  }
}

function openReceptionModal() {
  receptionForm.expeditionId = '';
  receptionForm.dateReception = toDateInput(new Date());
  receptionForm.statut = 'OK';
  receptionItems.value = [
    {
      uid: 1,
      typeElementPrefabriqueId: '',
      quantiteEnvoyee: '',
      quantiteRecue: '',
      quantiteEndommagee: '0',
      quantiteManquante: '0'
    }
  ];
  nextReceptionItemUid.value = 2;
  receptionFormError.value = null;
  showReceptionModal.value = true;
}

function closeReceptionModal() {
  showReceptionModal.value = false;
  receptionFormError.value = null;
}

function addReceptionItemRow() {
  receptionItems.value.push({
    uid: nextReceptionItemUid.value++,
    typeElementPrefabriqueId: '',
    quantiteEnvoyee: '',
    quantiteRecue: '',
    quantiteEndommagee: '0',
    quantiteManquante: '0'
  });
}

function removeReceptionItemRow(uid: number) {
  receptionItems.value = receptionItems.value.filter((item) => item.uid !== uid);
}

async function saveReception() {
  const expeditionId = toNumberOrNull(receptionForm.expeditionId);
  if (expeditionId === null) {
    receptionFormError.value = 'Veuillez sélectionner une expédition.';
    return;
  }
  if (!receptionForm.dateReception) {
    receptionFormError.value = 'La date de réception est requise.';
    return;
  }

  savingReception.value = true;
  receptionFormError.value = null;
  try {
    const created = await apiFetch('/receptions/receptions-site', {
      method: 'POST',
      body: {
        expeditionId,
        dateReception: receptionForm.dateReception,
        statut: receptionForm.statut
      }
    }) as { id: number };

    const validItems = receptionItems.value
      .map((item) => ({
        typeElementPrefabriqueId: toNumberOrNull(item.typeElementPrefabriqueId),
        quantiteEnvoyee: toNumberOrNull(item.quantiteEnvoyee),
        quantiteRecue: toNumberOrNull(item.quantiteRecue),
        quantiteEndommagee: toNumberOrNull(item.quantiteEndommagee),
        quantiteManquante: toNumberOrNull(item.quantiteManquante)
      }))
      .filter(
        (item): item is {
          typeElementPrefabriqueId: number;
          quantiteEnvoyee: number;
          quantiteRecue: number;
          quantiteEndommagee: number;
          quantiteManquante: number;
        } =>
          item.typeElementPrefabriqueId !== null &&
          item.quantiteEnvoyee !== null &&
          item.quantiteRecue !== null &&
          item.quantiteEndommagee !== null &&
          item.quantiteManquante !== null &&
          item.quantiteEnvoyee > 0
      );

    await Promise.all(
      validItems.map((item) =>
        apiFetch(`/receptions/receptions-site/${created.id}/items`, {
          method: 'POST',
          body: item
        })
      )
    );

    closeReceptionModal();
    await Promise.all([loadReceptions(), loadFluxData()]);
  } catch (err) {
    receptionFormError.value = err instanceof Error ? err.message : 'Erreur lors de la création de la réception';
  } finally {
    savingReception.value = false;
  }
}

function openInstallationModal() {
  editingInstallation.value = null;
  installationForm.siteId = selectedSiteId.value;
  installationForm.typeElementPrefabriqueId = '';
  installationForm.quantiteInstallee = '';
  installationForm.dateInstallation = toDateInput(new Date());
  installationFormError.value = null;
  showInstallationModal.value = true;
}

function openEditInstallationModal(installation: Installation) {
  editingInstallation.value = installation;
  installationForm.siteId = String(installation.siteId);
  installationForm.typeElementPrefabriqueId = String(installation.typeElementPrefabriqueId);
  installationForm.quantiteInstallee = String(installation.quantiteInstallee);
  installationForm.dateInstallation = installation.dateInstallation;
  installationFormError.value = null;
  showInstallationModal.value = true;
}

function closeInstallationModal() {
  showInstallationModal.value = false;
  editingInstallation.value = null;
  installationFormError.value = null;
}

async function saveInstallation() {
  const siteId = toNumberOrNull(installationForm.siteId);
  const typeElementPrefabriqueId = toNumberOrNull(installationForm.typeElementPrefabriqueId);
  const quantiteInstallee = toNumberOrNull(installationForm.quantiteInstallee);

  if (siteId === null || typeElementPrefabriqueId === null || quantiteInstallee === null || quantiteInstallee <= 0) {
    installationFormError.value = 'Veuillez renseigner les champs obligatoires.';
    return;
  }
  if (!installationForm.dateInstallation) {
    installationFormError.value = 'La date de pose est requise.';
    return;
  }

  savingInstallation.value = true;
  installationFormError.value = null;
  try {
    const payload = {
      typeElementPrefabriqueId,
      quantiteInstallee,
      dateInstallation: installationForm.dateInstallation
    };

    if (editingInstallation.value) {
      await apiFetch(`/installations/installations-site/${editingInstallation.value.id}`, {
        method: 'PATCH',
        body: payload
      });
    } else {
      await apiFetch(`/installations/sites/${siteId}/installations`, {
        method: 'POST',
        body: payload
      });
    }

    closeInstallationModal();
    await Promise.all([loadInstallations(), loadFluxData()]);
  } catch (err) {
    installationFormError.value = err instanceof Error ? err.message : 'Erreur lors de l’enregistrement de la pose';
  } finally {
    savingInstallation.value = false;
  }
}

async function deleteInstallation(installationId: number) {
  const confirmed = window.confirm('Supprimer cette pose ?');
  if (!confirmed) {
    return;
  }

  try {
    await apiFetch(`/installations/installations-site/${installationId}`, {
      method: 'DELETE'
    });
    await Promise.all([loadInstallations(), loadFluxData()]);
  } catch (err) {
    error.value = err instanceof Error ? err.message : 'Erreur lors de la suppression';
  }
}

function viewReception(reception: Reception) {
  router.push({ name: 'reception-detail', params: { id: reception.id } });
}

function resolveTypeLabel(typeId: number): string {
  const found = typesElements.value.find((type) => type.id === typeId);
  return found ? `${found.libelle} (${found.code})` : `Type #${typeId}`;
}

function getReceptionStatutVariant(statut: Reception['statut']): 'default' | 'primary' | 'success' | 'warning' | 'error' {
  switch (statut) {
    case 'OK':
      return 'success';
    case 'AVEC_DOMMAGES':
      return 'warning';
    case 'PARTIEL':
      return 'primary';
    default:
      return 'default';
  }
}

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString('fr-FR');
}

function toNumberOrNull(value: string): number | null {
  if (value.trim() === '') {
    return null;
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function toDateInput(date: Date) {
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
}
</script>

<style scoped>
.site-page {
  max-width: 1400px;
  margin: 0 auto;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-lg);
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-md);
}

.page-header h1 {
  margin: 0;
}

.page-actions {
  display: flex;
  gap: var(--spacing-sm);
}

.filters-card {
  padding: var(--spacing-md);
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: var(--spacing-md);
}

.kpis-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(190px, 1fr));
  gap: var(--spacing-md);
}

.kpi-card {
  padding: var(--spacing-lg);
  text-align: center;
}

.kpi-value {
  font-size: var(--font-size-4xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-primary);
}

.kpi-label {
  margin-top: var(--spacing-xs);
  color: var(--color-text-secondary);
  font-size: var(--font-size-sm);
}

.panel-section {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-sm);
}

.section-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.section-header h2 {
  margin: 0;
}

.modal-form {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.items-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.items-header h3 {
  margin: 0;
  font-size: var(--font-size-lg);
}

.reception-item-row {
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr 1fr auto;
  gap: var(--spacing-sm);
  align-items: end;
}

.form-error {
  padding: var(--spacing-sm);
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  background: #fee;
  color: #c33;
  font-size: var(--font-size-sm);
}

.form-actions {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
}

.error {
  padding: var(--spacing-md);
  background: #fee;
  border: 1px solid #fcc;
  border-radius: var(--radius-md);
  color: #c33;
}

@media (max-width: 1100px) {
  .reception-item-row {
    grid-template-columns: 1fr;
  }
}
</style>
